import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { EnvironmentProvider, useEnvironment } from './contexts/EnvironmentContext';
import { AuthProvider } from './contexts/AuthContext';
import { Sidebar } from './components/Layout/Sidebar';
import { ProtectedRoute } from './components/Layout/ProtectedRoute';
import { AdminMonitoringRoute } from './components/Layout/AdminMonitoringRoute';
import { BcUserPermissionsAudit } from './components/Audit/BcUserPermissionsAudit';
import { OrderList } from './components/Orders/OrderList';
import { OrderDetail } from './components/Orders/OrderDetail';
import { OrderTimeline } from './components/Orders/OrderTimeline';
import { PromotionList } from './components/Promotions/PromotionList';
import { PromotionDetail } from './components/Promotions/PromotionDetail';
import { CustomerGroupList } from './components/CustomerGroups/CustomerGroupList';
import { CustomerGroupDetail } from './components/CustomerGroups/CustomerGroupDetail';
import { CustomerList } from './components/Customers/CustomerList';
import { CustomerDetail } from './components/Customers/CustomerDetail';
import { Dashboard } from './components/Dashboard/Dashboard';
import { AMBRMockup } from './components/Dashboard/AMBRMockup';
import { ProductAnalyticsDashboard } from './components/Dashboard/ProductAnalyticsDashboard';
import { MobileNavMockup } from './components/Layout/MobileNavMockup';
import { MobileOrdersMockup } from './components/Layout/MobileOrdersMockup';
import { AMBRPaymentStatusMockup } from './components/Dashboard/AMBRPaymentStatusMockup';
import { MonitoringDashboard } from './components/Monitoring/MonitoringDashboard';
import { Login } from './components/Layout/Login';
import { useAuth } from './contexts/AuthContext';
import { apiClient } from './services/api';
import './App.css';

function AppRoutes() {
  const { environment } = useEnvironment();
  const { getAccessToken, logout, isAuthenticated, account } = useAuth();
  const navigate = useNavigate();

  // Set synchronously on every render so these are always in place before
  // any child component mounts and makes an API call. Using useEffect causes
  // a race: child effects (e.g. Dashboard fetchData) run before parent effects,
  // meaning the first API call goes out with no token → 401 → spurious logout.
  apiClient.setAccessTokenGetter(getAccessToken);
  apiClient.setAccountEmailGetter(() => account?.username ?? null);
  apiClient.setAuthErrorHandler(async () => {
    // Guard: only call MSAL logout when the user is actually authenticated.
    // During MSAL's redirect-callback initialization, isAuthenticated is briefly
    // false; a 401 during that window must not trigger a logout() call or it
    // interrupts the auth flow and sends the user back to the login screen.
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error during logout:', error);
      navigate('/login', { replace: true });
    }
  });

  useEffect(() => {
    document.title = 'CFAInstitute BigCommerce Viewer';
  }, [environment]);

  return (
    <div className="App">
      <Sidebar />
      <main className="main-content">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Navigate to="/analytics" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <OrderList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:id"
            element={
              <ProtectedRoute>
                <OrderDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:id/timeline"
            element={
              <ProtectedRoute>
                <OrderTimeline />
              </ProtectedRoute>
            }
          />
          <Route
            path="/promotions"
            element={
              <ProtectedRoute>
                <PromotionList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/promotions/:id"
            element={
              <ProtectedRoute>
                <PromotionDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer-groups"
            element={
              <ProtectedRoute>
                <CustomerGroupList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer-groups/:id"
            element={
              <ProtectedRoute>
                <CustomerGroupDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <CustomerList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers/:id"
            element={
              <ProtectedRoute>
                <CustomerDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/monitoring"
            element={
              <ProtectedRoute>
                <AdminMonitoringRoute>
                  <MonitoringDashboard />
                </AdminMonitoringRoute>
              </ProtectedRoute>
            }
          />
          <Route path="/mobile-nav-mockup" element={<MobileNavMockup />} />
          <Route path="/mobile-orders-mockup" element={<MobileOrdersMockup />} />
          <Route path="/ambr-payment-status" element={<AMBRPaymentStatusMockup />} />
          <Route
            path="/ambr-mockup"
            element={
              <ProtectedRoute>
                <AMBRMockup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <ProductAnalyticsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ambr"
            element={
              <ProtectedRoute>
                <ProductAnalyticsDashboard defaultSku="AMBR" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/internal/bc-permissions-audit"
            element={
              <ProtectedRoute>
                <AdminMonitoringRoute>
                  <BcUserPermissionsAudit />
                </AdminMonitoringRoute>
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,   // 2 min — data stays fresh
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <EnvironmentProvider>
          <Router>
            <AppRoutes />
          </Router>
        </EnvironmentProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
